import { useRef } from "react";

import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { hideToast, showToast } from "app/actions/toast_actions";
import type { IToast } from "app/components/React/Toast/types";
import { quotaQueryClient } from "app/pages/sales-intelligence/modules/quota/queries/client";
import { exportToExcelToastThunk } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/effects";

import { exportAppsToExcelService } from "../../services/export/appsExcelExportService";
import type { AppsExcelDownloadRequestDto, AppsExportRequestDto, AppsSearchRequestDto } from "../../types/dto";
import { createAppsExcelExportRequestDto } from "../../utils/dto";

type AppsExportFn = (dto: AppsExcelDownloadRequestDto) => Promise<{ success: boolean }>;

export const useAppsExcelExportMutation = (searchRequestDto: AppsSearchRequestDto, exportFn: AppsExportFn) => {
    const dispatch = useDispatch();

    const toastRef = useRef<IToast>(null);

    return useMutation({
        mutationFn(data: AppsExportRequestDto) {
            return exportFn(createAppsExcelExportRequestDto(searchRequestDto, data));
        },
        onMutate(data: AppsExportRequestDto) {
            const count = "count" in data ? data.count : data.apps.length;

            toastRef.current = exportAppsToExcelService.createToast(
                exportAppsToExcelService.getExportingToastTitle(count),
            );

            dispatch(
                showToast(
                    exportAppsToExcelService.getToastComponent(toastRef.current.children as string),
                    "success",
                    toastRef.current.timestamp,
                ),
            );
        },
        async onSettled(data) {
            if (toastRef.current) {
                dispatch(hideToast(toastRef.current));
            }

            if (data?.success) {
                dispatch(exportToExcelToastThunk(exportAppsToExcelService.getSuccessToastTitle()));

                await quotaQueryClient.invalidateExportQuota();
            } else {
                dispatch(exportToExcelToastThunk(exportAppsToExcelService.getErrorToastTitle()));
            }
        },
    });
};
